import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Badge, Tooltip, Typography, Button } from "@material-ui/core";
import { format, parseISO } from "date-fns";
import { Facebook, Instagram, MessageCircle as WhatsApp } from "lucide-react";

const useStyles = makeStyles(theme => ({
  cardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  connectionTag: {
    background: "#000000",
    color: "#FFF",
    marginRight: theme.spacing(1),
    padding: "2px 6px",
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  lastMessageTime: {
    textAlign: "right",
    color: theme.palette.text.secondary,
    fontSize: "0.85em",
    whiteSpace: "nowrap",
  },
  lastMessageTimeUnread: {
    textAlign: "right",
    color: theme.palette.success.main,
    fontWeight: "bold",
    fontSize: "0.85em",
    whiteSpace: "nowrap",
  },
  cardButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    fontSize: "0.75em",
    padding: "4px 8px",
  },
  messagePreview: {
    fontSize: "0.9em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: theme.spacing(0.5),
  },
  cardMetadata: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
    flexWrap: "wrap",
  },
  channelIcon: {
    verticalAlign: "middle",
    fontSize: "16px",
  },
  
}));

const getChannelIcon = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998" }} className="channel-icon" />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c" }} className="channel-icon" />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366" }} className="channel-icon" />;
    default:
      return null;
  }
};

/**
 * KanbanCard - Memoized card component for optimal rendering performance
 * Prevents re-renders when parent re-renders with same props
 */
const KanbanCard = React.memo(({ ticket, onCardClick }) => {
  const classes = useStyles();
  const isUnread = Number(ticket.unreadMessages) > 0;
  
  // Check if ticket was updated today
  const updatedDate = parseISO(ticket.updatedAt);
  const today = new Date();
  const isSameDayResult = 
    updatedDate.getFullYear() === today.getFullYear() &&
    updatedDate.getMonth() === today.getMonth() &&
    updatedDate.getDate() === today.getDate();
  
  const timeFormat = isSameDayResult
    ? format(updatedDate, "HH:mm")
    : format(updatedDate, "dd/MM/yyyy");

  const handleClick = (e) => {
    e.stopPropagation();
    onCardClick(ticket.uuid);
  };

  return (
    <div className={classes.cardContainer}>
      <div className={classes.cardHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Tooltip title={ticket.whatsapp?.name || ticket.channel}>
            {getChannelIcon(ticket.channel)}
          </Tooltip>
          <span style={{ fontSize: "0.9em", fontWeight: "500" }}>
            {ticket.contact.name}
          </span>
        </div>
        <Typography
          className={isUnread ? classes.lastMessageTimeUnread : classes.lastMessageTime}
          component="span"
          variant="body2"
        >
          {timeFormat}
        </Typography>
      </div>

      <div className={classes.messagePreview}>{ticket.contact.number}</div>

      {ticket.lastMessage && (
        <div className={classes.messagePreview} title={ticket.lastMessage}>
          {ticket.lastMessage}
        </div>
      )}

      <div className={classes.cardMetadata}>
        <Button
          className={classes.cardButton}
          onClick={handleClick}
          size="small"
          variant="contained"
        >
          Ver Ticket
        </Button>
        {ticket?.user && (
          <Badge className={classes.connectionTag}>
            {ticket.user.name.toUpperCase()}
          </Badge>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memoization
  // Return true if props are equal (skip re-render), false otherwise
  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.unreadMessages === nextProps.ticket.unreadMessages &&
    prevProps.ticket.lastMessage === nextProps.ticket.lastMessage &&
    prevProps.ticket.updatedAt === nextProps.ticket.updatedAt &&
    prevProps.onCardClick === nextProps.onCardClick
  );
});

KanbanCard.displayName = "KanbanCard";

export default KanbanCard;
